import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Menu, X, Phone, PawPrint, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [tappedItem, setTappedItem] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Kittens', path: '/kittens' },
    { name: 'Videos', path: '/videos' },
    { name: 'Parents', path: '/gallery' },
    { name: 'About Us', path: '/about' },
    { name: 'Kitten Process', path: '/adoption-process' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Contact', path: '/contact' },
  ];

  // More precise active check
  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname === path;
  };

  // Handle menu open/close with smooth animation
  const toggleMenu = () => {
    if (isMenuOpen) {
      setIsAnimating(true);
      setTimeout(() => {
        setIsMenuOpen(false);
        setIsAnimating(false);
      }, 250);
    } else {
      setIsMenuOpen(true);
    }
  };

  // Close menu on navigation with visual feedback
  const handleNavClick = (e, path) => {
    e.preventDefault();
    setTappedItem(path);
    
    setTimeout(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setIsMenuOpen(false);
        setIsAnimating(false);
        setTappedItem(null);
        navigate(path);
      }, 150);
    }, 100);
  };

  // Close menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
    setIsAnimating(false);
    setTappedItem(null);
  }, [location.pathname]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-rose-100/50 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Mobile: Compact header, Desktop: Normal */}
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo - Smaller on mobile */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <div className="w-8 h-8 lg:w-10 lg:h-10 bg-gradient-to-br from-rose-100 to-pink-50 rounded-full flex items-center justify-center">
              <PawPrint className="w-4 h-4 lg:w-5 lg:h-5 text-[#E85D75]" />
            </div>
            <div>
              <span className="text-lg lg:text-2xl font-serif font-bold text-stone-800 tracking-tight block">
                KITTENSTATE
              </span>
              <p className="text-[9px] lg:text-[10px] uppercase tracking-[0.15em] lg:tracking-[0.2em] text-stone-400 font-medium -mt-0.5">
                Premium Scottish Kittens
              </p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navLinks.map((link) => (
              <Link
                key={link.path + link.name}
                to={link.path}
                data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  isActive(link.path)
                    ? 'text-[#E85D75] bg-rose-50'
                    : 'text-stone-600 hover:text-[#E85D75] hover:bg-rose-50/50'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* CTA Button - Desktop */}
          <div className="hidden md:flex items-center gap-4">
            <a href="tel:929-333-1936">
              <Button
                data-testid="header-call-button"
                className="bg-[#E85D75] text-white hover:bg-[#D64560] shadow-lg shadow-[#E85D75]/25 hover:shadow-xl hover:shadow-[#E85D75]/35 rounded-full px-6 py-5 font-medium transition-all transform hover:-translate-y-0.5"
              >
                <PawPrint className="w-4 h-4 mr-2" />
                Get in Touch
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-button"
            onClick={toggleMenu}
            className="lg:hidden p-2 text-stone-600 hover:text-[#E85D75] hover:bg-rose-50 rounded-lg transition-all"
          >
            <div className="relative w-5 h-5">
              <span className={`absolute inset-0 transition-all duration-200 ${isMenuOpen ? 'opacity-100 rotate-0' : 'opacity-0 -rotate-90'}`}>
                <X className="w-5 h-5" />
              </span>
              <span className={`absolute inset-0 transition-all duration-200 ${isMenuOpen ? 'opacity-0 rotate-90' : 'opacity-100 rotate-0'}`}>
                <Menu className="w-5 h-5" />
              </span>
            </div>
          </button>
        </div>

        {/* Mobile Menu with Animation */}
        <div
          className={`lg:hidden overflow-hidden transition-all duration-250 ease-out ${
            isMenuOpen 
              ? isAnimating 
                ? 'max-h-0 opacity-0' 
                : 'max-h-[600px] opacity-100'
              : 'max-h-0 opacity-0'
          }`}
        >
          <nav className="py-2 border-t border-rose-100/30" data-testid="mobile-menu">
            <div className="px-2">
              {navLinks.map((link, index) => (
                <a
                  key={link.path + link.name}
                  href={link.path}
                  onClick={(e) => handleNavClick(e, link.path)}
                  data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`flex items-center justify-between px-4 py-3 rounded-xl text-[15px] font-medium transition-all duration-200 ${
                    tappedItem === link.path 
                      ? 'bg-rose-100 scale-[0.98]'
                      : isActive(link.path)
                        ? 'text-[#E85D75] bg-rose-50/70'
                        : 'text-stone-700 hover:text-[#E85D75] hover:bg-rose-50/40 active:bg-rose-100 active:scale-[0.98]'
                  }`}
                  style={{ 
                    animationDelay: `${index * 40}ms`,
                    animation: isMenuOpen && !isAnimating ? 'slideInFade 0.3s ease-out forwards' : 'none',
                    opacity: isMenuOpen && !isAnimating ? 1 : 0
                  }}
                >
                  <span>{link.name}</span>
                  {isActive(link.path) && (
                    <span className="w-1.5 h-1.5 rounded-full bg-[#E85D75]" />
                  )}
                </a>
              ))}
            </div>
            
            {/* Mobile Call Button - Premium styling */}
            <div className="mt-3 pt-3 border-t border-rose-100/30 px-4 pb-2">
              <a href="tel:929-333-1936" className="block">
                <Button
                  data-testid="mobile-call-button"
                  className="w-full bg-gradient-to-r from-[#E85D75] to-[#D64560] text-white rounded-xl py-4 text-sm font-semibold shadow-lg shadow-[#E85D75]/25 active:scale-[0.98] active:shadow-md transition-all duration-200"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Call: 929-333-1936
                </Button>
              </a>
            </div>
          </nav>
        </div>
      </div>

      {/* Overlay when menu is open */}
      {isMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/10 backdrop-blur-[1px] -z-10 transition-opacity duration-200"
          onClick={toggleMenu}
          style={{ top: '64px' }}
        />
      )}

      {/* CSS for premium animations */}
      <style>{`
        @keyframes slideInFade {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </header>
  );
}
